// ─────────────────────────────────────────────────────────────────────────────
// Marketplaces — canonical list + helpers for the per-scan listing badge.
// A "marketplace link" is just { name, url } — name is one of MARKETPLACE_NAMES
// (case-insensitive at the data layer; we normalise to display-cased here),
// url is optional (a collector may add a marketplace before they have the URL,
// or paste it later).
// ─────────────────────────────────────────────────────────────────────────────

export const MARKETPLACE_NAMES = [
  "Depop", "Grailed", "eBay", "Etsy", "Vestiaire", "Poshmark", "Mercari", "Vinted",
  // "Personal Site" — the seller's own website. URL input, no prefix.
  "Personal Site",
  // "In person" — physical sales. Free-text address OR a Google/Apple Maps
  // share URL. If it parses as a URL it renders as a clickable "open in maps"
  // CTA; if it's plain text it renders as a non-clickable address label.
  "In person",
] as const;
export type MarketplaceName = typeof MARKETPLACE_NAMES[number];

export interface MarketplaceLink {
  name: string;
  url: string;
}

// Force the display casing back onto a (possibly free-form) marketplace string
// stored in the DB so old `posted_to: "depop"` rows render as "Depop".
export function normaliseMarketplaceName(raw: string): string {
  if (!raw) return "";
  const lower = raw.trim().toLowerCase();
  const match = MARKETPLACE_NAMES.find((m) => m.toLowerCase() === lower);
  return match ?? raw.trim();
}

// Defensive parser — Supabase returns marketplace_links as Json, and pre-
// migration scans default to []. Anything malformed is stripped silently so a
// bad row never breaks rendering.
export function parseMarketplaceLinks(raw: unknown): MarketplaceLink[] {
  if (!Array.isArray(raw)) return [];
  const out: MarketplaceLink[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const name = typeof r.name === "string" ? normaliseMarketplaceName(r.name) : "";
    const url  = typeof r.url  === "string" ? r.url.trim() : "";
    if (!name) continue;
    out.push({ name, url });
  }
  return out;
}

// Best-effort: trim, ensure protocol so target=_blank actually opens it.
export function ensureUrlProtocol(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function isValidUrl(url: string): boolean {
  if (!url.trim()) return true; // empty is allowed (placeholder before they have the link)
  try {
    new URL(ensureUrlProtocol(url));
    return true;
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-marketplace expected hostnames. Used for *soft* validation: we don't
// block saving on a mismatch, we just warn the collector so a Depop link doesn't
// silently end up under "Grailed". Each entry is a list of hostname suffixes
// (no protocol, no path) that count as "this is the right marketplace".
// ─────────────────────────────────────────────────────────────────────────────
const MARKETPLACE_DOMAINS: Record<string, string[]> = {
  depop:      ["depop.com"],
  grailed:    ["grailed.com"],
  ebay:       ["ebay.com", "ebay.co.uk", "ebay.de", "ebay.fr", "ebay.it", "ebay.ca", "ebay.com.au"],
  etsy:       ["etsy.com"],
  vestiaire:  ["vestiairecollective.com", "vestiaire.com"],
  poshmark:   ["poshmark.com", "poshmark.ca"],
  mercari:    ["mercari.com", "jp.mercari.com"],
  vinted:     ["vinted.com", "vinted.co.uk", "vinted.fr", "vinted.de", "vinted.it", "vinted.es", "vinted.nl", "vinted.pl"],
};

/** Hostnames that should NOT be flagged on any marketplace (link shorteners). */
const SHORTENER_DOMAINS = new Set([
  "bit.ly", "t.co", "tinyurl.com", "lnk.to", "linktr.ee",
]);

export function expectedDomainsFor(name: string): string[] {
  return MARKETPLACE_DOMAINS[name.trim().toLowerCase()] ?? [];
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-marketplace shop URL prefixes. Used to prefill the input when a
// collector adds a new shop in Settings — they only need to type their
// username, not the whole URL. Returns "" for marketplaces that don't have a
// stable per-user URL pattern (e.g. "In person").
// ─────────────────────────────────────────────────────────────────────────────
const MARKETPLACE_URL_PREFIXES: Record<string, string> = {
  depop:     "https://www.depop.com/",
  grailed:   "https://www.grailed.com/",
  ebay:      "https://www.ebay.com/usr/",
  etsy:      "https://www.etsy.com/shop/",
  vestiaire: "https://www.vestiairecollective.com/members/",
  poshmark:  "https://poshmark.com/closet/",
  mercari:   "https://www.mercari.com/u/",
  vinted:    "https://www.vinted.com/member/",
};

export function urlPrefixFor(name: string): string {
  return MARKETPLACE_URL_PREFIXES[name.trim().toLowerCase()] ?? "";
}

/**
 * Returns null if the URL looks fine for this marketplace, or a short human
 * warning string otherwise. Empty / invalid URLs return null — those are
 * handled by the existing isValidUrl gate at save time.
 */
export function urlDomainWarning(name: string, url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  let host = "";
  try {
    host = new URL(ensureUrlProtocol(trimmed)).hostname.toLowerCase();
  } catch {
    return null; // malformed — let isValidUrl handle it
  }
  // Strip leading "www." to make suffix checks predictable.
  const cleanHost = host.replace(/^www\./, "");
  if (SHORTENER_DOMAINS.has(cleanHost)) return null;
  const expected = expectedDomainsFor(name);
  if (expected.length === 0) return null; // unknown marketplace, no hint
  const ok = expected.some((d) => cleanHost === d || cleanHost.endsWith(`.${d}`));
  if (ok) return null;
  return `This doesn't look like a ${name} link — expected ${expected[0]}.`;
}
